import { message } from 'antd';
import { get } from 'lodash';
import { formatQueryParams } from 'utils/formatQueryParams';
import { USER_CENTER_URL, TZ_CLIENT_ID, TZ_CLIENT_SECRET } from 'constants/env';
import { Auth } from 'utils';

const needFailedFeedbackRequest = [];
const needFeedBackRequest = [];
const whiteList = ['/uni/oauth/token'];

// 刷新 token 的标志，防止多次刷新
let isRefreshing = false;
// 存储待重试的请求队列
let failedQueue = [];
// 上次刷新 token 的时间戳
let lastRefreshTime = 0;
// 刷新 token 的最小间隔时间（毫秒）
const REFRESH_INTERVAL = 5000;

// 处理请求队列
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// 刷新 token 的函数
const refreshAccessToken = async () => {
  const refresh_token = Auth.get('refresh_token');

  if (!refresh_token) {
    throw new Error('No refresh token available');
  }

  // 检查是否在 5 秒内已经刷新过
  const now = Date.now();
  if (now - lastRefreshTime < REFRESH_INTERVAL) {
    console.warn('刷新 token 过于频繁，请稍后再试');
    throw new Error('Token refresh too frequent, please wait');
  }

  // 立即记录刷新时间，防止并发请求重复触发
  lastRefreshTime = now;

  try {
    // 使用 URL 查询参数方式，client_id 和 client_secret 使用固定值
    const url = `${USER_CENTER_URL}/uni/oauth/token?grant_type=refresh_token&client_id=1603500352454&client_secret=6d437f855f0af2c1b2c32c084ba5a2ac&refresh_token=${refresh_token}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (data.access_token) {
      Auth.set('token', data.access_token);
      if (data.refresh_token) {
        Auth.set('refresh_token', data.refresh_token);
      }

      // 处理等待的请求
      processQueue(null, data.access_token);

      return data.access_token;
    } else {
      throw new Error('Failed to refresh token');
    }
  } catch (error) {
    // 刷新失败，清空等待队列
    processQueue(error, null);
    // 刷新 token 失败，清除认证信息并跳转登录
    // Auth.del();
    throw error;
  }
};

function parseJSON(response) {
  return response.json();
}

function showFeedback(response, path) {
  if (needFeedBackRequest.includes(path)) {
    if (get(response, 'success')) {
      message.success(get(response, 'msg'));
    } else {
      message.error(get(response, 'msg'));
    }
  }

  if (needFailedFeedbackRequest.includes(path)) {
    if (!get(response, 'success')) {
      message.error(get(response, 'msg'));
    }
  }

  return response;
}

export async function request(path, query = {}, method = 'GET', payload = {}) {
  const customHeader = get(payload, 'customHeader', {}); // 自定义Header
  const responseType = get(payload, 'responseType', 'json');
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    schoolId: Auth.get('schoolId'),
    Authorization: Auth.get('token') ? 'bearer' + Auth.get('token') : '',
    username: Auth.get('username'),
    sid: Auth.get('sid'),
    operatorName: encodeURIComponent(Auth.get('operatorName') || ''),
    ...customHeader,
  };
  let url = USER_CENTER_URL + path;
  const { openApiToken, ...params } = query;

  const purePath = path.replace(/\?.*$/g, '');
  // if (!whiteList.includes(purePath) && !openApiToken) {
  //   return;
  // }

  // if (openApiToken && !whiteList.includes(purePath)) {
  //   Object.assign(headers, { Authorization: 'bearer' + openApiToken });
  // }

  const options = {
    method,
    // credentials: 'include',
    // mode: 'cors',
    headers,
    responseType,
  };

  // 标记是否已经重试过，防止无限循环
  let hasRetried = false;

  if (method === 'POST') {
    Object.assign(options, { body: JSON.stringify(params) });
  }

  if (method === 'GET') {
    url = USER_CENTER_URL + path + formatQueryParams(params);
  }

  return fetch(url, options)
    .then(async (response) => {
      // 如果返回 401，尝试刷新 token
      if (response.status === 401) {
        // 如果已经重试过，直接抛出错误，不再刷新
        if (hasRetried) {
          console.error('请求重试后仍然返回 401，停止刷新 token');
          throw new Error('Authentication failed after retry');
        }

        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const newToken = await refreshAccessToken();
            isRefreshing = false;

            // 标记已重试
            hasRetried = true;

            // 使用新 token 重试原请求
            options.headers.Authorization = 'bearer ' + newToken;
            const retryResponse = await fetch(url, options);
            return parseJSON(retryResponse).then((res) => showFeedback(res, path));
          } catch (error) {
            isRefreshing = false;
            throw error;
          }
        } else {
          // 如果正在刷新 token，将请求加入队列
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token) => {
                // 标记已重试
                hasRetried = true;
                options.headers.Authorization = 'bearer ' + token;
                fetch(url, options)
                  .then(parseJSON)
                  .then((res) => {
                    resolve(showFeedback(res, path));
                  })
                  .catch((err) => {
                    reject(err);
                  });
              },
              reject,
            });
          });
        }
      }

      return parseJSON(response).then((res) => showFeedback(res, path));
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

export async function postRequestTemp(path, query = {}) {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (Auth.isAuthenticated()) {
    Object.assign(headers, { token: Auth.get('token') });
  }

  const options = {
    method: 'POST',
    headers,
  };

  return fetch(USER_CENTER_URL + path + formatQueryParams(query), options)
    .then(parseJSON)
    .catch((error) => {});
}
